import React from 'react';
import { cx } from '@emotion/css';
import { withTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import { Responsive } from '../../utils';
import { ITheme, themed } from '../../themer';

// React must stay imported for babel-plugin-emotion v10 to compile `cx` correctly.
// Referencing it directly keeps it "used" so the build doesn't depend on a
// suppression directive that would itself start erroring if this ever changes.
// eslint-disable-next-line no-unused-expressions
React;

export type IResponsiveImageSource = Responsive.IValueOrResponsiveValue<string>;

export interface IResponsivePictureProps extends Record<string, unknown> {
	source: Responsive.IValueOrResponsiveValue<string>;
	className?: string;
	alt?: string;
	rounded?: boolean;
	roundedOnMobile?: boolean;
	objectFitCover?: boolean;
	noMaxWidth?: boolean;
}

type IResponsivePicturePropsWithTheme = IResponsivePictureProps & {
	theme: ITheme;
};

export const ResponsivePicture = withTheme(
	({
		source,
		theme,
		className,
		alt,
		objectFitCover = false,
		rounded = false,
		noMaxWidth = false,
		...rest
	}: IResponsivePicturePropsWithTheme) => {
		let defaultImageSource: string;
		let responsiveImageSources: Responsive.IBreakpointValues<string> = {};

		if (Array.isArray(source)) {
			[defaultImageSource, responsiveImageSources] = source;
		} else {
			defaultImageSource = source;
		}

		const breakpoints = theme.breakpoints.map(({ name }) => name);

		// Image sources should be ordered from smallest to largest
		breakpoints.reverse();

		const imageClassName = cx(
			objectFitCover && 'objectFitCover',
			rounded && 'rounded',
			noMaxWidth && 'no-max-width',
		);

		return (
			<picture className={className} {...rest}>
				{breakpoints.map(
					(breakpoint) =>
						breakpoint in responsiveImageSources && (
							<source
								key={breakpoint}
								media={`(${Responsive.getMediaQueryForBreakpoint(breakpoint)})`}
								srcSet={responsiveImageSources[breakpoint]}
							/>
						),
				)}
				<StyledImage
					src={defaultImageSource}
					rounded={rounded}
					alt={alt}
					className={imageClassName}
					loading="lazy"
				/>
			</picture>
		);
	},
);

const StyledImage = styled.img<{ rounded: boolean }, ITheme>`
	width: 100%;
	&.objectFitCover {
		object-fit: cover;
	}
	&.rounded {
		border-radius: ${themed(({ image }) => image.borderRadius.default)};
	}
`;
